/**
 * The project's single style kit.
 *
 * Every form control, field label, card surface, semantic banner, checkbox
 * and pill in the app draws its class strings from here. Before this module the
 * same strings were re-declared per file — 7 `labelClass`, 6 `inputClass`, 9
 * card-chrome copies, 11 banner hues and 13 checkbox idioms, all drifting
 * independently. Tweak once here, the whole product follows.
 *
 * Canon references (`design-aesthetics.md`):
 * - §53 (R1 ruling) — every interactive control is `h-9` / `rounded-md`. No
 *   other height or radius is legal. Heights in §7/§15/§42 are superseded.
 * - §15 — field label is 11px / 590 / uppercase / 0.06em tracking.
 * - §31 — the semantic tone map: emerald success, amber warn, red error,
 *   muted neutral. Tone is picked by outcome, never for decoration.
 * - §3 — brand indigo `#5e6ad2`, accent violet `#7170ff`, focus border
 *   `#7170ff/55` light · `/75` dark. Error border `red-500/60`.
 *
 * Style *objects* live here too (not just classes) because the 510/590 weights
 * and negative tracking of `DESIGN.md` §3 cannot be expressed in Tailwind
 * utilities without breaching the `font-semibold` guard.
 */

/* ── Controls ─────────────────────────────────────────────────────────────── */

/** Text input / select trigger chrome. §53: `h-9 rounded-md`, focus is border-only (§13 — no ring on fields). */
export const inputClass =
  'w-full h-9 px-3 bg-background dark:bg-white/[0.02] border border-border/60 dark:border-white/[0.08] ' +
  'hover:border-border/80 dark:hover:border-white/[0.14] rounded-md ' +
  'text-foreground dark:text-[#f7f8f8] placeholder:text-muted-foreground/50 dark:placeholder:text-[#62666d] ' +
  'focus:outline-none focus-visible:border-[#7170ff]/55 dark:focus-visible:border-[#7170ff]/75 ' +
  'transition-colors duration-150'

export const inputStyle = {
  fontSize: '13px',
  fontWeight: 510,
  letterSpacing: '-0.13px',
} as const

/** Textarea — same chrome as the input, taller padding, no resize handle. */
export const textareaClass =
  'w-full px-3 py-2.5 bg-background dark:bg-white/[0.02] border border-border/60 dark:border-white/[0.08] ' +
  'hover:border-border/80 dark:hover:border-white/[0.14] rounded-md ' +
  'text-foreground dark:text-[#f7f8f8] placeholder:text-muted-foreground/50 dark:placeholder:text-[#62666d] ' +
  'focus:outline-none focus-visible:border-[#7170ff]/55 dark:focus-visible:border-[#7170ff]/75 ' +
  'transition-colors duration-150 resize-none leading-relaxed'

/** Error state for a field — border only, never a ring (§13). */
export const errorBorderClass = 'border-red-500/60 focus-visible:border-red-500/70'

/**
 * The auth-screen field chrome, byte-identical across LoginPage, RegisterPage,
 * ForgotPasswordPage and ResetPasswordPage (it was declared four times).
 *
 * Deliberately NOT `inputClass`: the auth screens are the project's reference
 * implementation for atmosphere and type (§2–§11) and run their own taller
 * `h-11` field on a white card with a `login-input` autofill hook.
 *
 * Stage-4 ruling on the height §53 deferred here: **the auth field stays `h-11`.**
 * §53 governs the in-app product controls, which sit at 13px in dense forms next
 * to a Select and a DatePicker. The auth field is 14px, carries a `pl-10` leading
 * icon, and is the only control on its screen — shrinking it to `h-9` would break
 * the §2–§11 atmosphere reference for no consistency win, because there is no
 * other control on the page to be consistent *with*. Recorded in
 * `design-aesthetics.md` §53.
 *
 * The focus *token*, unlike the height, is not carved out: it is now the canon
 * `#7170ff/55` light · `/75` dark (§3, §56.5). It previously read
 * `focus:border-[#5e6ad2]/70` with no dark partner at all — a half-fix that
 * rendered as no focus indication on the dark auth card.
 */
export const authInputClass =
  'login-input w-full h-11 rounded-md bg-white border border-black/[0.08] text-[#0f1011] ' +
  'dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8] ' +
  'placeholder:text-[#7a7d83] dark:placeholder:text-[#62666d] ' +
  'pl-10 pr-3 transition-colors duration-150 ' +
  'hover:border-black/[0.14] hover:bg-[#fafafa] ' +
  'dark:hover:border-white/[0.14] dark:hover:bg-white/[0.03] ' +
  'focus:outline-none focus:border-[#7170ff]/55 focus:bg-white ' +
  'dark:focus:border-[#7170ff]/75 dark:focus:bg-white/[0.03]'

export const authInputStyle = {
  fontSize: '14px',
  fontWeight: 400,
  letterSpacing: '-0.13px',
} as const

/* ── Labels ───────────────────────────────────────────────────────────────── */

/**
 * The field label (§15). One idiom for the whole product: uppercase eyebrow,
 * 11px / 590 / 0.06em. Pair with `labelStyle`.
 */
export const labelClass = 'block mb-1.5 uppercase text-muted-foreground dark:text-[#8a8f98]'

export const labelStyle = {
  fontSize: '11px',
  fontWeight: 590,
  letterSpacing: '0.06em',
} as const

/** Section eyebrow — smaller and wider-tracked than a field label. */
export const sectionEyebrowClass =
  'block uppercase text-muted-foreground/80 dark:text-[#62666d]'

export const sectionEyebrowStyle = {
  fontSize: '10px',
  fontWeight: 590,
  letterSpacing: '0.09em',
} as const

/** Card / section title — the 13px/590 pairing used by every analytics + billing card header. */
export const cardTitleStyle = {
  fontSize: '13px',
  fontWeight: 590,
  letterSpacing: '-0.13px',
} as const

/* ── Surfaces ─────────────────────────────────────────────────────────────── */

/**
 * Card chrome. Applied on `<Card className={cardChromeClass}>` or a raw div —
 * `rounded-lg` because a card is a surface, not a control (§53 governs
 * controls only).
 */
export const cardChromeClass =
  'rounded-lg border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02]'

/** Hairline divider between sections. */
export const dividerClass = 'h-px w-full bg-border/50 dark:bg-white/[0.06] my-4'

/** Field group container — consistent vertical rhythm inside a section. */
export const sectionClass = 'space-y-4'

/* ── Icon chips (§14: confine chroma to a small chip) ─────────────────────── */

const iconChipBase = 'h-9 w-9 rounded-md border flex items-center justify-center'

export const iconChipClass =
  `${iconChipBase} bg-[#5e6ad2]/[0.10] dark:bg-[#5e6ad2]/[0.14] border-[#5e6ad2]/20 dark:border-[#5e6ad2]/25`

export const iconChipAmberClass =
  `${iconChipBase} bg-amber-500/[0.10] dark:bg-amber-500/[0.14] border-amber-500/20 dark:border-amber-500/25`

export const iconChipNeutralClass =
  `${iconChipBase} bg-muted/70 dark:bg-white/[0.04] border-border/50 dark:border-white/[0.06]`

/* ── Semantic banners (§31 tone map) ─────────────────────────────────────── */

const bannerBase = 'flex items-start gap-2 px-3 py-2.5 rounded-md border'

/** Neutral hint / explanation. Indigo — the house voice, not a warning. */
export const infoBannerClass =
  `${bannerBase} bg-[#5e6ad2]/[0.06] dark:bg-[#5e6ad2]/[0.10] ` +
  'border-[#5e6ad2]/15 dark:border-[#5e6ad2]/20 text-[#5e6ad2] dark:text-[#a5b4fc]'

/** Something the user should weigh before continuing. */
export const warnBannerClass =
  `${bannerBase} bg-amber-500/[0.08] dark:bg-amber-500/[0.10] ` +
  'border-amber-500/20 dark:border-amber-500/25 text-amber-700 dark:text-amber-300'

/** Something is wrong / will be destroyed. */
export const dangerBannerClass =
  `${bannerBase} bg-red-500/[0.08] dark:bg-red-500/[0.10] ` +
  'border-red-500/20 dark:border-red-500/25 text-red-700 dark:text-red-300'

/** Confirmation that something landed. */
export const successBannerClass =
  `${bannerBase} bg-emerald-500/[0.08] dark:bg-emerald-500/[0.10] ` +
  'border-emerald-500/20 dark:border-emerald-500/25 text-emerald-700 dark:text-emerald-300'

export type BannerTone = 'info' | 'warn' | 'danger' | 'success'

export const bannerClass: Record<BannerTone, string> = {
  info: infoBannerClass,
  warn: warnBannerClass,
  danger: dangerBannerClass,
  success: successBannerClass,
}

export const bannerTextStyle = {
  fontSize: '12px',
  lineHeight: 1.55,
  letterSpacing: '-0.1px',
} as const

/* ── Checkboxes ───────────────────────────────────────────────────────────── */

/**
 * The one checkbox. `accent-[#5e6ad2]` rather than the retiring Tailwind brand
 * token plus a focus ring — §13 bans focus rings on form fields, and the token
 * itself is being withdrawn.
 */
export const checkboxInputClass = 'h-4 w-4 rounded accent-[#5e6ad2] cursor-pointer'

/**
 * Inline `<label>` wrapping a checkbox and its text.
 *
 * This is the *only* checkbox row idiom the product has. A bordered `h-9`
 * variant used to live here too (`checkboxRowClass`) and was removed after an
 * audit found zero consumers and zero adoptable sites: every checkbox list in
 * the app is either borderless-by-design (the materials tag filter, canon
 * §"Filter by tag" — indigo tint on selection, hairline separators, no
 * per-row border), already inside a bordered container whose rows would nest
 * a second border (`MaterialSelector`, `FieldSelectionPanel`,
 * `MultiSelectCheckbox`), a table cell (every import modal), or a two-line
 * card taller than `h-9` (`LaunchCampaignModal` rows,
 * `EnrichContactsConfig` blocks). Don't reintroduce it without a real site.
 */
export const checkboxLabelClass =
  'inline-flex items-center gap-2 text-foreground dark:text-[#f7f8f8] cursor-pointer'

/* ── Pills (§48 — selected filter is a soft indigo tint, never a solid fill) ─ */

export const pillBaseClass =
  'inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full border transition-colors duration-150 ' +
  'cursor-pointer select-none'

export const pillActiveClass =
  'bg-[#5e6ad2]/10 text-[#5e6ad2] border-[#5e6ad2]/25 ' +
  'dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30'

export const pillInactiveClass =
  'bg-transparent text-muted-foreground border-border/60 hover:border-border/80 hover:text-foreground ' +
  'dark:text-[#8a8f98] dark:border-white/[0.08] dark:hover:border-white/[0.14] dark:hover:text-[#f7f8f8]'

export const pillTextStyle = {
  fontSize: '12px',
  fontWeight: 510,
  letterSpacing: '-0.05px',
} as const
